import { useQueryClient } from '@tanstack/react-query';
import {
	type ErrorComponentProps,
	DefaultGlobalNotFound,
	isRedirect,
	Navigate,
	useLocation,
	useRouter,
} from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@worldcoo/ui/components/button';
import { AlertCircle } from 'lucide-react';
import { useServerFn } from '@tanstack/react-start';
import { errorReportData, extractErrorInfo, logErrorFn } from '@/lib/data';
import { isClientFaultRpcError, shouldReportFrontendError } from '@/lib/frontend-error-filter';
import { useEphemeralStore } from '@/lib/app-store';
import { isUnauthorizedError, ngoLoginRedirectPath } from '@/lib/ngo-session';
import { recentBreadcrumbs } from '@/lib/session-actions';

export function DefaultCatchBoundary(props: ErrorComponentProps) {
	if ('isNotFound' in props.error) {
		return <DefaultGlobalNotFound />;
	}

	if (isRedirect(props.error)) {
		return <RedirectHandler redirectError={props.error} />;
	}

	// The session cookie died after the page mounted. Navigate to login instead of showing the error
	// UI — and never report it: an expired session is an expected auth transition, not a defect.
	// Only NGO-scoped procedures throw UNAUTHORIZED, so this cannot fire on the public portals.
	if (isUnauthorizedError(props.error)) {
		return <NgoLoginRedirect />;
	}

	return <CatchBoundary {...props} />;
}

function RedirectHandler(props: { redirectError: unknown }) {
	const router = useRouter();

	useEffect(() => {
		if (isRedirect(props.redirectError)) {
			router.navigate(props.redirectError.options).catch((err) => {
				console.error('Failed to navigate after redirect:', err);
			});
		}
	}, [router, props.redirectError]);

	return null;
}

function NgoLoginRedirect() {
	const { pathname } = useLocation();
	// Snapshot the path on first render — see the same note in routes/ngos/route.tsx: reading the live
	// location on a later pass would set the post-login target to `/login` itself.
	const [redirect] = useState(() => ngoLoginRedirectPath(pathname));
	return <Navigate to="/ngos/login" search={{ redirect }} replace />;
}

export const VERSION_SKEW_RELOAD_KEY = 'worldcoo:version-skew-reload';
export const VERSION_SKEW_RELOAD_COOLDOWN_MS = 30_000;

/**
 * Whether a version-skew reload fired recently enough to still be in flight. During that window the
 * DOM is being torn down while React streaming SSR scripts still execute, so errors raised in it are
 * benign fallout (e.g. "Cannot read properties of null (reading 'parentNode')") and must not be
 * reported. Every handler that reports frontend errors checks this first — the global ones in
 * `router.tsx` and the checkout route's own Apple Pay listener.
 */
export function isVersionSkewReloadInProgress() {
	if (typeof window === 'undefined') return false;
	const lastReload = window.sessionStorage.getItem(VERSION_SKEW_RELOAD_KEY);
	return !!lastReload && Date.now() - parseInt(lastReload) < VERSION_SKEW_RELOAD_COOLDOWN_MS;
}

/**
 * Check if we can safely reload for a version skew error.
 * Returns false if we already reloaded recently (within cooldown), preventing infinite reload loops
 * that can happen during ECS rolling deployments when sticky sessions route to mismatched tasks.
 */
function canReloadForVersionSkew() {
	if (typeof window === 'undefined') return false;
	return !isVersionSkewReloadInProgress();
}

function CatchBoundary(props: ErrorComponentProps) {
	const queryClient = useQueryClient();
	const logError = useServerFn(logErrorFn);
	const { t } = useLingui();
	const clearCheckoutCache = useEphemeralStore((state) => state.clearCheckoutCache);
	const hasLoggedError = useRef(false);

	const errorMessage = props.error.message ?? '';
	const errorName = props.error instanceof Error ? props.error.name : '';
	// A stale/deleted chunk after a deploy surfaces with browser-specific wording: Firefox says
	// "error loading dynamically imported module", Chrome "Failed to fetch dynamically imported module",
	// Safari "Importing a module script failed", and bundlers may throw a named `ChunkLoadError`. Match
	// all of them so the version-skew reload path fires everywhere instead of only on Firefox — otherwise
	// Chrome's failures fall through and get ticketed (TEC-3415 / TEC-3413).
	const isModuleLoadError =
		errorMessage.includes('error loading dynamically imported module') ||
		errorMessage.includes('Failed to fetch dynamically imported module') ||
		errorMessage.includes('Importing a module script failed') ||
		errorName === 'ChunkLoadError';
	const isStaleServerFnError =
		errorMessage.includes('Server function info not found') ||
		errorMessage.includes('Server function module not resolved') ||
		errorMessage.includes('Server function module export not resolved') ||
		errorMessage.includes('expected content-type header to be set');
	const isVersionSkewError = isModuleLoadError || isStaleServerFnError;
	const shouldReloadForVersionSkew = isVersionSkewError && canReloadForVersionSkew();

	console.error('DefaultCatchBoundary Error:', props.error);

	// Log error to backend
	// If the error was derived from an update that is not retrocompatible (a version skew)
	// this will bust the cache on memory and immediately sync with disk
	useEffect(() => {
		if (hasLoggedError.current) return;
		hasLoggedError.current = true;

		queryClient.clear();
		clearCheckoutCache();

		if (shouldReloadForVersionSkew) {
			window.sessionStorage.setItem(VERSION_SKEW_RELOAD_KEY, String(Date.now()));
			window.location.reload();
			return;
		}

		if (isVersionSkewError) return;

		// A FORBIDDEN the server refused on purpose is the caller's fault, not a system error — judged on
		// the error object, same as the global handlers.
		if (isClientFaultRpcError(props.error)) return;

		const errorInfo = extractErrorInfo(props.error);
		// Drop environmental / third-party / transient noise (browser extensions, page translators,
		// reCAPTCHA/GA, cross-origin script loads on unsupported nested subdomains, stale-chunk aborts)
		// so it doesn't spam PORTAL_FRONTEND_ERROR tickets — same filter the global handlers in
		// router.tsx apply. Without this, an error that escapes to the React boundary is ticketed even
		// when its type/message is already declared non-actionable.
		if (!shouldReportFrontendError(errorInfo.name, errorInfo.message)) return;
		logError({
			data: errorReportData('', errorInfo, { source: 'boundary', breadcrumbs: recentBreadcrumbs() }),
		}).catch((logErr) => {
			console.error('Failed to log error to backend:', logErr);
		});
	}, [queryClient, props.error, logError, clearCheckoutCache, shouldReloadForVersionSkew, isVersionSkewError]);

	if (shouldReloadForVersionSkew) return null;

	return (
		<div className="flex flex-col gap-4 text-center">
			<div className="bg-destructive/10 mx-auto flex size-12 items-center justify-center rounded-full">
				<AlertCircle className="text-destructive size-6" />
			</div>

			<div className="flex flex-col gap-2">
				<h2 className="text-xl font-semibold">{t`Something went wrong`}</h2>
				<p className="text-muted-foreground text-sm">
					{t`We're sorry, but something unexpected happened. Please try again or refresh the page.`}
				</p>
			</div>

			{import.meta.env.DEV && props.error instanceof Error && (
				<details className="text-left">
					<summary className="text-muted-foreground cursor-pointer text-sm font-medium">{t`Technical details`}</summary>
					<div className="bg-muted mt-2 rounded-md p-3">
						<p className="font-mono text-xs wrap-break-word">{props.error.message}</p>
						{props.error.stack && (
							<pre className="text-muted-foreground mt-2 max-h-40 overflow-auto font-mono text-xs">
								{props.error.stack}
							</pre>
						)}
					</div>
				</details>
			)}

			<div className="flex flex-col gap-2">
				<Button onClick={() => window.location.reload()} variant="default">
					{t`Try again`}
				</Button>
				<Button onClick={() => (window.location.href = '/')} variant="outline">
					{t`Go to homepage`}
				</Button>
			</div>
		</div>
	);
}
